/**
 * The deal, animated: card backs fly from the dealer to each player in Euchre's usual rhythm (3-2-3-2, then 2-3-2-3,
 * starting at the dealer's left), then the last four go to the middle as the kitty.
 *
 * Why it looks the way it does:
 * - The flying cards live in their own layer on <body>, outside the table, so the table can redraw as often as
 *   it likes (bubbles, bots, the timer) without cutting the animation short.
 * - While the deal runs, the real cards are hidden (body[data-dealing]); each flying card lands exactly where its
 *   real card sits, and when the deal finishes the real cards take over and your own hand flips face up.
 * - Only `transform` and `opacity` move, which phones hand to their graphics chip, so it stays smooth on older devices.
 * - Skipped when the player's device asks for reduced motion, when the tab is in the background, and when you
 *   arrive in the middle of a hand (a reload or reconnect shows the cards straight away, as before).
 */

import type { RoomSnapshot, Seat } from '@euchre/core';
import { POSITION_CLASS, position } from './names.js';

/** Last deal seen per table: -1 while in the lobby, so the first deal of a game animates too. */
const seenDeal = new Map<string, number>();
let running: { layer: HTMLElement; timer: number } | null = null;

const FLIGHT_MS = 340;
const STAGGER_MS = 58;
/** Cards per player in the two rounds, starting at the dealer's left (the traditional 3-2 / 2-3 deal). */
const ROUND_ONE = [3, 2, 3, 2];
const ROUND_TWO = [2, 3, 2, 3];

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** Call after every render. Starts the deal animation when a new hand has just been dealt at this table. */
export function dealAnimation(room: RoomSnapshot | null): void {
  if (!room) {
    stop();
    return;
  }
  const g = room.game;
  if (!g || room.status === 'lobby') {
    stop();
    seenDeal.set(room.code, -1);
    return;
  }
  const before = seenDeal.get(room.code);
  seenDeal.set(room.code, g.dealNumber);
  if (before === g.dealNumber) return;
  const fresh = (g.phase === 'deal_options' || g.phase === 'bidding1') && g.tricks.length === 0 && g.currentTrick.length === 0;
  // Arriving mid-game (a reload or reconnect) shows the cards straight away; the first hand of a game still animates,
  // because "Just me and bots" can go straight from the home screen to the first deal without a lobby in between.
  if (before === undefined && g.handNumber !== 1) return;
  if (!fresh || reducedMotion() || document.hidden) return;
  start(room, g.dealer);
}

function start(room: RoomSnapshot, dealer: Seat): void {
  stop();
  const from = document.querySelector<HTMLElement>(`.plate.${POSITION_CLASS[position(room, dealer)]} .nameplate`);
  if (!shown(from)) return;
  const src = centreOf(from);

  // Who gets each card, in dealing order.
  const order: Seat[] = [];
  for (const round of [ROUND_ONE, ROUND_TWO]) {
    round.forEach((n, i) => {
      const seat = ((dealer + 1 + i) % 4) as Seat;
      for (let k = 0; k < n; k++) order.push(seat);
    });
  }

  const layer = document.createElement('div');
  layer.className = 'deal-layer';
  layer.setAttribute('aria-hidden', 'true');
  document.body.append(layer);
  document.body.dataset.dealing = 'on';

  const landed = new Map<Seat, number>();
  let delay = 0;
  for (const seat of order) {
    const nth = landed.get(seat) ?? 0;
    landed.set(seat, nth + 1);
    const target = targetFor(room, seat, nth);
    if (target) fly(layer, src, target, delay);
    delay += STAGGER_MS;
  }
  // The kitty: four cards to the middle, a beat after the last player card.
  const kitty = document.querySelector<HTMLElement>('.kitty-stack');
  if (shown(kitty)) {
    delay += STAGGER_MS * 2;
    for (let k = 0; k < 4; k++) fly(layer, src, { el: kitty, rotate: 0 }, delay + k * 30, k * 3);
    delay += 90;
  }

  const timer = window.setTimeout(finish, delay + FLIGHT_MS + 60);
  running = { layer, timer };
}

/** Where the nth card for a seat lands: that card in your hand, or the nth back in another player's fan. */
function targetFor(room: RoomSnapshot, seat: Seat, nth: number): Target | null {
  const pos = position(room, seat);
  if (pos === 0 && room.you.seat !== null) {
    const card = document.querySelectorAll<HTMLElement>('.hand .card')[nth];
    if (shown(card)) return { el: card, rotate: 0 };
  }
  const back = document.querySelectorAll<HTMLElement>(`.plate.${POSITION_CLASS[pos]} .backs .card`)[nth];
  // The fans on the left and right are turned sideways.
  if (shown(back)) return { el: back, rotate: pos === 1 || pos === 3 ? 90 : 0 };
  const plate = document.querySelector<HTMLElement>(`.plate.${POSITION_CLASS[pos]} .nameplate`);
  // No visible fan (small screens hide the other players' cards): the card flies into their name tag and fades.
  return shown(plate) ? { el: plate, rotate: 0, vanish: true } : null;
}

type Target = { el: HTMLElement; rotate: number; vanish?: boolean };

function fly(layer: HTMLElement, src: { x: number; y: number }, target: Target, delay: number, nudge = 0): void {
  const r = target.el.getBoundingClientRect();
  // Size of the card itself (offsetWidth ignores the sideways rotation of the side fans).
  const isCard = target.el.classList.contains('card') || target.el.classList.contains('kitty-stack');
  const w = isCard ? target.el.offsetWidth - (target.el.classList.contains('kitty-stack') ? 8 : 0) : 46;
  const hgt = isCard ? target.el.offsetHeight - (target.el.classList.contains('kitty-stack') ? 8 : 0) : 64;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;

  const card = document.createElement('div');
  card.className = 'card back deal-card';
  card.style.width = `${w}px`;
  card.style.height = `${hgt}px`;
  card.style.left = `${cx - w / 2 + nudge}px`;
  card.style.top = `${cy - hgt / 2 + nudge}px`;
  layer.append(card);

  const dx = src.x - cx;
  const dy = src.y - cy;
  const spin = (Math.random() - 0.5) * 24;
  card.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) rotate(${spin}deg) scale(0.55)`, opacity: 0 },
      { opacity: 1, offset: 0.15 },
      target.vanish
        ? { transform: `translate(0, 0) rotate(${spin}deg) scale(0.3)`, opacity: 0 }
        : { transform: `translate(0, 0) rotate(${target.rotate}deg) scale(1)`, opacity: 1 },
    ],
    { duration: FLIGHT_MS, delay, easing: 'cubic-bezier(0.2, 0.7, 0.25, 1)', fill: 'both' },
  );
}

function finish(): void {
  stop();
  // Your own cards turn face up as they take over from the flying backs.
  for (const c of document.querySelectorAll<HTMLElement>('.hand .card.face')) {
    c.animate([{ transform: 'rotateY(90deg)' }, { transform: 'rotateY(0deg)' }], { duration: 220, easing: 'ease-out' });
  }
}

function stop(): void {
  if (!running) return;
  window.clearTimeout(running.timer);
  running.layer.remove();
  running = null;
  delete document.body.dataset.dealing;
}

/** On screen with a size (small screens hide some parts of the table with display: none). */
function shown(el: HTMLElement | null | undefined): el is HTMLElement {
  return !!el && el.offsetWidth > 0 && el.offsetHeight > 0;
}

function centreOf(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}
